import type { ChargeOutcome } from '@evimo/types/constants/session/session.labels';
import type { SessionQualification } from '@evimo/types/enums/session-qualification';
import { SessionQualification as SessionQualificationEnum } from '@evimo/types/enums/session-qualification';

export type QualificationTagColor =
  | 'success'
  | 'error'
  | 'warning'
  | 'processing'
  | 'default';

const QUALIFICATION_LABEL_KEY: Record<SessionQualification, string> = {
  SUCCESSFUL: 'successful',
  FAILED: 'failed',
  ABORTED: 'aborted',
  NOT_CLOSED: 'notClosed',
  REFUSED_AT_START: 'refusedAtStart',
  IN_PROGRESS: 'inProgress',
};

const QUALIFICATION_TOOLTIP_KEY: Record<SessionQualification, string> = {
  SUCCESSFUL: 'successfulHint',
  FAILED: 'failedHint',
  ABORTED: 'abortedHint',
  NOT_CLOSED: 'notClosedHint',
  REFUSED_AT_START: 'refusedAtStartHint',
  IN_PROGRESS: 'inProgressHint',
};

export function qualificationLabelKey(value: SessionQualification): string {
  return QUALIFICATION_LABEL_KEY[value];
}

export function qualificationTooltipKey(value: SessionQualification): string {
  return QUALIFICATION_TOOLTIP_KEY[value];
}

/** Qualifications surfaced under “Anomaly Detected” (not normal in-progress / successful). */
export function isAnomalyQualification(
  value: SessionQualification | null | undefined,
): boolean {
  if (!value) {
    return false;
  }
  return (
    value !== SessionQualificationEnum.IN_PROGRESS &&
    value !== SessionQualificationEnum.SUCCESSFUL
  );
}

/** Show when qualification is not a normal in-progress / successful bucket. */
export function shouldShowAnomalyQualification(
  qualification: SessionQualification | null | undefined,
): boolean {
  return isAnomalyQualification(qualification);
}

export function isCompletedSuccessfully(input: {
  outcome: ChargeOutcome | null | undefined;
  success: boolean | null | undefined;
  qualification: SessionQualification | null | undefined;
}): boolean {
  if (input.outcome !== 'completed') {
    return false;
  }
  if (
    input.qualification != null &&
    isAnomalyQualification(input.qualification)
  ) {
    return false;
  }
  if (input.success === true) {
    return true;
  }
  return input.qualification === SessionQualificationEnum.SUCCESSFUL;
}

export function qualificationTagColor(
  value: SessionQualification,
): QualificationTagColor {
  switch (value) {
    case 'SUCCESSFUL':
      return 'success';
    case 'FAILED':
      return 'error';
    case 'ABORTED':
      return 'warning';
    case 'NOT_CLOSED':
      return 'processing';
    case 'REFUSED_AT_START':
      return 'default';
    case 'IN_PROGRESS':
      return 'processing';
    default:
      return 'default';
  }
}
